import { Link } from "react-router";
import type { Alert } from "@/types/models";
import { rankFlocksToActOn, type FlockPriority } from "@/pages/Dashboard/flockPriority";
import SnoozeMenu from "@/pages/Dashboard/SnoozeMenu";

// Synthesised "which flock to act on" — flocks ranked by the severity and count
// of their alerts, each with its combined reasons + the single recommended
// action. Turns the flat per-issue alerts list into a decision, and (on the
// dashboard) owns every flock problem so nothing is listed twice. Renders
// nothing when no flock has an alert. Shared by the dashboard (all flocks,
// snoozable) and the Flock Economics comparison page (top-3 summary, no snooze).
export default function FlocksToActOn({
  alerts,
  onSnooze,
  limit,
}: {
  alerts: Alert[];
  onSnooze?: (flock: FlockPriority, days?: number) => void;
  limit?: number;
}) {
  const flocks = rankFlocksToActOn(alerts, limit);
  if (flocks.length === 0) return null;

  return (
    <div className="mb-6 card p-5 sm:p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">Flocks to act on</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {flocks.length === 1 ? "1 flock" : `${flocks.length} flocks`}
        </span>
      </div>
      <ol className="space-y-2">
        {flocks.map((f, i) => (
          <FlockRow key={f.flock_id} flock={f} rank={i + 1} onSnooze={onSnooze} />
        ))}
      </ol>
    </div>
  );
}

function FlockRow({
  flock,
  rank,
  onSnooze,
}: {
  flock: FlockPriority;
  rank: number;
  onSnooze?: (flock: FlockPriority, days?: number) => void;
}) {
  const dot = flock.criticalCount > 0 ? "bg-error-500" : "bg-warning-500";
  const issues = flock.alerts.length;
  return (
    <li className="flex items-start gap-1">
      <Link
        to={`/flocks/${flock.flock_id}`}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-white/[0.02]"
      >
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
              <span className="text-gray-400 dark:text-gray-500">{rank}. </span>
              {flock.flock_name}
            </p>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {issues} {issues === 1 ? "issue" : "issues"}
            </span>
          </div>
          {/* Combined reasons — the "why" behind the ranking. */}
          <div className="mt-1 flex flex-wrap gap-1">
            {flock.reasons.map((r, i) => (
              <span
                key={i}
                className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                {r}
              </span>
            ))}
          </div>
          {/* The single recommended next move — the "decision". */}
          {flock.action && (
            <p className="mt-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">
              <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                →{" "}
              </span>
              {flock.action}
            </p>
          )}
        </div>
      </Link>
      {/* Mute this flock — flock alerts may be snoozed indefinitely. */}
      {onSnooze && (
        <SnoozeMenu allowIndefinite ariaLabel="Snooze this flock" onPick={(days) => onSnooze(flock, days)} />
      )}
    </li>
  );
}
